// Today (/staff): the one list. Everything waiting on a founder, grouped by urgency, each group with its read time. Rows act
// in place or open the record. The list comes from GET /api/staff/today (pr/crm/today.py). "Nothing needs you" prints only
// when every source was read and is current; otherwise the page names what wasn't read.
import { useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, cents } from '../../../lib'
import { Busy, Button, Err, Fresh, Page, Strip, ago, dayOf, errorText, num, useCursor, useHashScroll, useKit, when } from '../kit'
import { useRecordActions } from '../records/actions'

type Suggestion = { external_customer_id: string; name: string | null; email: string | null }
type RowT = {
  kind: string; key: string; title: string; sub?: string; since?: string; to?: string; href?: string; owner?: string; late?: boolean
  id?: string; company?: string; amount_cents?: number | null; earning_cents?: number | null; points?: number | null; lines?: number; points_rate?: string
  partner?: string; partner_to?: string; company_id?: string; partner_id?: string; billing_email?: string | null; customer_ref?: string | null; note?: string | null
  suggestions?: Suggestion[]; reason?: string; evidence?: Record<string, unknown>; companies?: { id: string; name: string; note: string }[]; account_domain?: string | null
  attempts?: number; due_on?: string; starts?: string; actions?: { label: string; to: string }[]
  days?: number | null; quiet?: boolean; no_touch?: boolean; next_at?: string; detail?: string; old?: boolean
}
type BlockT = {
  limit?: number | null
  key: string; title: string; read: boolean; current: boolean; total: number; open: number; rows: RowT[]; to: string | null; to_label: string | null; empty: string
  fresh: { label: string; at: string | null; ok: boolean } | null; note: string | null; read_at: string | null
}
type GroupT = { key: string; title: string; blocks: BlockT[]; total: number; checked: string[]; unread: string[]; stale: string[]; note?: string | null }
type SourceT = { id: string; label: string; at: string | null; ok: boolean; state: string; failed: boolean }
type TodayT = {
  at: string; who: 'mine' | 'everyone'; me: string | null; owner: boolean; groups: GroupT[]; open: number; calm: boolean; unread: string[]; sources: SourceT[]
  verdict: { n: number | null; text: string; to: string }[]
  next_call: { name: string; company?: string; at: string | null; to: string; source: { label: string; at: string | null; ok: boolean } | null } | null
  state: { line: { title: string; sub: string | null } | null; loop: { pulse_age_s: number | null; live: boolean; stopped: boolean } | null; payouts_paused: boolean; card_waiting: number | null; send_ready: number | null }
}
type FeedRow = { kind: string; at: string; title: string; sub: string; path: string }

const KEY = ['staff', 'today']
const FEED_KINDS: Record<string, string> = {
  '': 'Everything', signup: 'Sign-ups', company: 'New companies', partner: 'New partners', file: 'Files in', payout: 'Payouts asked',
  card: 'Card requests', trip: 'Trips', staff: 'Decisions',
}
const REASON: Record<string, string> = {
  conflict: 'Already linked to another company', shared_domain: 'Email domain shared by more than one company', two_claimants: 'Two companies say this is them',
  owner_change: 'May have changed owner', overlap: 'Two customer records overlap',
}

function list(words: string[]): string {
  return words.length <= 1 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
}

export function TodayPage() {
  const [params, setParams] = useSearchParams()
  const who = params.get('who') === 'mine' || params.get('who') === 'everyone' ? params.get('who')! : ''
  const q = useQuery({
    queryKey: [...KEY, who],
    queryFn: () => api<TodayT>('GET', `/api/staff/today${who ? `?who=${who}` : ''}`),
    refetchInterval: 30_000,
  })
  const d = q.data
  const navigate = useNavigate()
  const a = useRecordActions({})
  const rows = useMemo(() => d?.groups.flatMap((g) => g.blocks.filter((b) => !FOLDED.includes(b.key)).flatMap((b) => b.rows.slice(0, peek(b)))) ?? [], [d])
  const { cur } = useCursor(rows, (r) => r.key, (r) => { if (r.to) navigate(r.to) })
  useHashScroll(!!d)
  return (
    <Page title="Today" testid="staff-home" right={d?.me ? (
      <Strip testid="today-who" value={d.who} onChange={(k) => setParams({ who: k }, { replace: true })}
        items={[{ key: 'mine', label: 'Mine and unowned' }, { key: 'everyone', label: 'Everyone' }]} />
    ) : <span className="st-note">Everyone's work</span>}>
      {a.node}
      <div className="st-row"><Button onClick={() => void a.task()} data-testid="today-add-task">Add a to-do</Button></div>
      {q.isLoading ? <Busy what="Reading what needs you" /> : q.error ? <Err error={q.error} /> : d ? (
        <>
          <Verdict d={d} />
          <StateLine d={d} />
          <div className="st-td-board" data-cur={cur ?? undefined}>
            <div className="st-td-list">{d.groups.filter((g) => g.key === 'talk' || g.key === 'send').map((g) => <Group key={g.key} g={g} cur={cur} />)}</div>
            <div className="st-td-list">{d.groups.filter((g) => g.key === 'decide' || g.key === 'broken').map((g) => <Group key={g.key} g={g} cur={cur} />)}</div>
          </div>
          <Feed />
          <InboxHealth />
        </>
      ) : null}
    </Page>
  )
}

function StateLine({ d }: { d: TodayT }) {
  const s = d.state
  return (
    <div className="st-td-state">
      <p data-testid="growth-state">
        <strong className={s.loop?.stopped ? 'st-bad' : undefined}>{s.line?.title ?? "Can't tell if campaigns are sending"}</strong>
        {s.line?.sub ? <span className="st-muted" data-testid="growth-limiter">{s.line.sub}</span> : null}
        {s.payouts_paused ? <span className="st-bad st-strong" data-testid="today-payouts-paused">Payouts paused</span> : null}
      </p>
      <Fresh at={d.at} label="Today" live limit={120} testid="today-read" />
    </div>
  )
}

/** The day in plain numbers, counted by the server from the rows below, so every word is true of the page. */
function Verdict({ d }: { d: TodayT }) {
  const nothing = d.open === 0 && !d.calm
  return (
    <div className="st-td-top">
      {d.calm ? <p className="st-td-verdict" data-testid="growth-calm">Nothing needs you</p>
        : nothing ? <p className="st-td-verdict st-bad" data-testid="today-verdict">Nothing found, but {list(d.unread)} {d.unread.length === 1 ? 'was' : 'were'} not read</p>
        : (
          <p className="st-td-verdict" data-testid="today-verdict">
            {d.verdict.map((p, i) => <span key={p.to}>{i ? ', ' : ''}<a className={p.n === null ? 'st-bad' : p.n ? undefined : 'st-muted'} href={p.to}>{i ? p.text.charAt(0).toLowerCase() + p.text.slice(1) : p.text}</a></span>)}
          </p>
        )}
      <NextCall c={d.next_call} />
    </div>
  )
}

/** The call that matters now, with the age of the list it came from, so nobody hunts for it. */
function NextCall({ c }: { c: TodayT['next_call'] }) {
  if (!c) return null
  const time = c.at ? new Date(c.at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : null
  return (
    <p className="st-td-next" data-testid="today-next-call">
      <Link className="st-link" to={c.to}>{time ? `Call ${time}` : 'Next call'}, {c.name}{c.company ? `, ${c.company}` : ''}</Link>
      {c.source ? <span className="st-muted">, {c.source.at ? `list ${ago(c.source.at)} old` : 'list not read'}</span> : null}
    </p>
  )
}

// Blocks that fold shut: nothing in them needs someone today.
const FOLDED = ['waiting', 'todo']
const peek = (b: BlockT) => (b.key === 'deals' ? 5 : 3)

function Group({ g, cur }: { g: GroupT; cur: string | null }) {
  const lists = g.blocks.filter((b) => !FOLDED.includes(b.key))
  const shown = lists.filter((b) => b.total > 0 || !b.current)
  const quiet = lists.filter((b) => b.current && b.total === 0)
  const folded = g.blocks.filter((b) => FOLDED.includes(b.key) && b.total > 0)
  return (
    <section className="st-td-group" id={`group-${g.key}`} data-testid={`today-${g.key}`}>
      <div className="st-section-head">
        <h2 className="st-section-title">{g.title}<span>{g.total}</span></h2>
      </div>
      {g.note ? <p className="st-note" data-testid="today-replies-unread">{g.note}</p> : null}
      {shown.map((b) => <Block key={b.key} b={b} cur={cur} />)}
      {folded.map((b) => (
        <details key={b.key} id={b.key} className="st-td-details" data-testid={b.key === 'waiting' ? 'today-waiting' : 'today-todo'}>
          <Folded b={b} />
        </details>
      ))}
      {!shown.length && !folded.length ? <p className="st-note" data-testid={`today-${g.key}-checked`}>Nothing waiting</p> : null}
      {quiet.length ? <div className="st-td-anchors">{quiet.map((b) => <span key={b.key} id={b.key} data-testid={`staff-stuck-${b.key}`} />)}</div> : null}
    </section>
  )
}

/** What's inside a folded block. Old notes stay out of the way until someone asks for them. */
function Folded({ b }: { b: BlockT }) {
  const [old, setOld] = useState(false)
  const now = b.rows.filter((r) => !r.old)
  const stale = b.rows.length - now.length
  return (
    <>
      <summary>{b.title} ({now.length}){stale ? `, ${stale} old` : ''}</summary>
      {(old ? b.rows : now).map((r) => <Row key={r.key} r={r} cur={false} />)}
      {stale && !old ? <div><Button tone="quiet" onClick={() => setOld(true)} data-testid="today-show-old">Show {stale} old</Button></div> : null}
    </>
  )
}

function Block({ b, cur }: { b: BlockT; cur: string | null }) {
  const [open, setOpen] = useState(false)
  const rows = open ? b.rows : b.rows.slice(0, peek(b))
  const folded = b.rows.length - rows.length
  // One row can stand for many (small payouts, old sources), and it already says how many.
  const more = b.rows.some((r) => r.kind === 'small' || r.kind === 'sources') ? 0 : b.total - b.rows.length
  const quiet = b.key === 'deals' ? b.open : null
  return (
    <div className="st-td-block" id={b.key} data-testid={b.key === 'pipeline' ? 'growth-needs' : `staff-stuck-${b.key}`}>
      <div className="st-td-block-head">
        <h3>{b.title}{b.read ? <span>{b.total}{quiet ? `, ${quiet} quiet` : ''}</span> : null}</h3>
        <span className="st-row">
          {b.to && b.to_label ? <Link className="st-link st-td-go" to={b.to}>{b.to_label}</Link> : null}
        </span>
      </div>
      {!b.read ? <p className="st-note st-bad" data-testid={`today-unread-${b.key}`}>{b.title} not read</p>
        : !b.current && !b.total ? <p className="st-note" data-testid={`today-stale-${b.key}`}>Not read, so this may be out of date</p> : null}
      {b.note ? <p className="st-note">{b.note}</p> : null}
      {rows.map((r) => <Row key={r.key} r={r} cur={cur === r.key} />)}
      {folded > 0 ? <div><Button tone="quiet" onClick={() => setOpen(true)} data-testid={`today-more-${b.key}`}>Show {folded} more</Button></div>
        : more > 0 ? <p className="st-note">{b.to ? <Link className="st-link" to={b.to}>And {more} more</Link> : `And ${more} more`}</p> : null}
    </div>
  )
}

function Row({ r, cur }: { r: RowT; cur: boolean }) {
  const special: Record<string, ReactNode> = {
    receipt: <ReceiptActions r={r} />, link_request: <LinkRequestActions r={r} />, case: <CaseActions r={r} />, job: <JobActions r={r} />, task: <TaskActions r={r} />,
  }
  const testid = { receipt: 'staff-receipt', link_request: 'staff-link-request', case: 'staff-case', job: 'staff-job-row', mail: 'staff-unplaced-mail', signup: 'staff-signup-stuck' }[r.kind]
    ?? (r.kind === 'alert' ? `growth-need-${r.id ?? r.key}` : undefined)
  return (
    <div className={`st-td-row${cur ? ' cur' : ''}${r.kind === 'task' || r.kind === 'job' ? ' inline' : ''}`} data-row={r.key} data-testid={testid}>
      <div className="st-td-main">
        <span className="st-td-title">
          {r.detail ? <details className="st-td-note" data-testid="today-task-note"><summary>{r.title}</summary><p>{r.detail}</p></details> : r.href ? <a className="st-link" href={r.href}>{r.title}</a> : r.to ? <Link className="st-link" to={r.to}>{r.title}</Link> : r.title}
          {r.partner ? <> for {r.partner_to ? <Link className="st-link" to={r.partner_to}>{r.partner}</Link> : r.partner}</> : null}
        </span>
        {r.sub ? <span className="st-td-sub">{r.kind === 'job' ? <code>{r.sub}</code> : r.sub}</span> : null}
        <Timing r={r} />
      </div>
      {special[r.kind] ?? null}
      {r.actions?.length ? <div className="st-td-actions">{r.actions.map((a) => <Link key={a.to} className="st-btn quiet" to={a.to}>{a.label}</Link>)}</div> : null}
    </div>
  )
}

function Timing({ r }: { r: RowT }) {
  if (r.kind === 'deal') {
    if (r.next_at) return <span className="st-td-when">Next {dayOf(r.next_at)}</span>
    if (r.no_touch) return <span className="st-td-when" data-testid="today-deal-age">No touch on record</span>
    return <span className="st-td-when" title={when(r.since)} data-testid="today-deal-age">{r.days} d</span>
  }
  if (r.starts) return <span className="st-td-when">{when(r.starts)}</span>
  if (r.due_on) return <span className={`st-td-when${r.late ? ' st-bad' : ''}`}>Due {dayOf(r.due_on)}</span>
  if (!r.since || r.kind === 'bill') return null
  return <span className={`st-td-when${r.late ? ' st-bad' : ''}`} title={when(r.since)} data-testid="staff-waited">{ago(r.since)}</span>
}

/** One action on a row: confirm first when asked, lock while pending, refresh the list after, and say what failed. */
/** The feed stays as it is: reloading its first page under older ones could leave a gap. */
const notFeed = (q: { queryKey: readonly unknown[] }) => q.queryKey[2] !== 'feed'

function useRowAction<B>(path: (id: string) => string) {
  const qc = useQueryClient()
  const { toast } = useKit()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body?: B; done: string }) => api<unknown>('POST', path(id), body),
    onSuccess: (_, v) => { toast(v.done); qc.invalidateQueries({ queryKey: KEY, predicate: notFeed }); qc.invalidateQueries({ queryKey: ['staff', 'counts'] }) },
    onError: (e) => toast(errorText(e), { bad: true }),
  })
}

function ReceiptActions({ r }: { r: RowT }) {
  const decide = useRowAction<{ approve: boolean; reason?: string }>((id) => `/api/staff/invoices/${id}/decide`)
  const { confirm } = useKit()
  const [reason, setReason] = useState('')
  const amount = r.amount_cents ? cents(r.amount_cents) : 'no amount'
  const points = r.points ? `${r.points.toLocaleString('en-US')} points` : 'no points'
  const count = async () => {
    const ok = await confirm({ title: 'Count this receipt', confirm: 'Count it', body: <>{r.company}: {amount}{r.lines ? ` across ${r.lines} ${r.lines === 1 ? 'line' : 'lines'}` : ''}. This adds {points}{r.points_rate ? ` at ${r.points_rate}` : ''}.</> })
    if (ok.ok) decide.mutate({ id: r.id!, body: { approve: true }, done: `Counted ${points}` })
  }
  const down = async () => {
    const ok = await confirm({ title: 'Turn down this receipt', confirm: 'Turn down', danger: true, body: <>{r.company}: {amount}{reason ? `. Why: ${reason}` : ''}.</> })
    if (ok.ok) decide.mutate({ id: r.id!, body: { approve: false, reason: reason || undefined }, done: 'Turned down' })
  }
  return (
    <div className="st-td-actions">
      <span className="st-td-sub" data-testid="staff-receipt-points">{points}{r.points_rate ? ` at ${r.points_rate}` : ''}</span>
      <a className="st-btn" href={`/api/staff/files/${r.id}`} target="_blank" rel="noreferrer" data-testid="staff-receipt-open">Open receipt</a>
      <Button tone="primary" disabled={decide.isPending} onClick={count} data-testid="staff-receipt-approve">Count it</Button>
      <input className="st-input st-td-grow" placeholder="Why not" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Why not" />
      <Button tone="quiet" disabled={decide.isPending} onClick={down} data-testid="staff-receipt-reject">Turn down</Button>
    </div>
  )
}

function LinkRequestActions({ r }: { r: RowT }) {
  const yes = useRowAction<{ external_customer_id: string }>((id) => `/api/staff/link-requests/${id}/confirm`)
  const no = useRowAction<{ reason?: string }>((id) => `/api/staff/link-requests/${id}/decline`)
  const { confirm } = useKit()
  const hints = r.suggestions ?? []
  const [ext, setExt] = useState(hints[0]?.external_customer_id ?? r.customer_ref ?? '')
  const [reason, setReason] = useState('')
  const busy = yes.isPending || no.isPending
  const facts = [`Billing email ${r.billing_email ?? 'not given'}`, r.customer_ref ? `their customer id ${r.customer_ref}` : null, r.note ? `"${r.note}"` : null].filter(Boolean).join(', ')
  const go = async () => {
    const ok = await confirm({ title: 'Confirm this customer', confirm: 'Confirm', body: <>{r.company} becomes customer {ext.trim()} at {r.partner}.</> })
    if (ok.ok) yes.mutate({ id: r.id!, body: { external_customer_id: ext.trim() }, done: 'Confirmed' })
  }
  const drop = async () => {
    const ok = await confirm({ title: 'Decline this request', confirm: 'Decline', danger: true, body: <>{r.company} is told {r.partner} could not match them. Your reason stays here and isn't sent.</> })
    if (ok.ok) no.mutate({ id: r.id!, body: { reason: reason || undefined }, done: 'Declined' })
  }
  return (
    <>
      <p className="st-td-sub st-td-facts">{facts}</p>
      <div className="st-td-actions">
        <label className="st-row">Customer record
          <input className="st-input" list={`sug-${r.id}`} value={ext} onChange={(e) => setExt(e.target.value)} placeholder="cus_…" data-testid="staff-link-request-customer" />
          <datalist id={`sug-${r.id}`}>{hints.map((s) => <option key={s.external_customer_id} value={s.external_customer_id}>{[s.name, s.email].filter(Boolean).join(', ')}</option>)}</datalist>
        </label>
        <Button tone="primary" disabled={!ext.trim() || busy} onClick={go} data-testid="staff-link-request-confirm">Confirm</Button>
        <input className="st-input st-td-grow" placeholder="Why not (kept here, not sent)" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Why not" />
        <Button tone="quiet" disabled={busy} onClick={drop} data-testid="staff-link-request-decline">Decline</Button>
      </div>
    </>
  )
}

const EVIDENCE: Record<string, string> = { claimants: 'Who claims it', note: 'Note', account: 'Account', domain: 'Domain' }
/** Evidence is whatever the match rule stored: words for each value, never raw JSON. */
function evidenceValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '–'
  if (v === true) return 'Yes'
  if (v === false) return 'No'
  if (Array.isArray(v)) return v.map(evidenceValue).join('; ')
  if (typeof v === 'object') return Object.values(v as Record<string, unknown>).map(evidenceValue).join(', ')
  return String(v)
}

function CaseActions({ r }: { r: RowT }) {
  const decide = useRowAction<{ outcome: string; company_id?: string }>((id) => `/api/staff/cases/${id}/decide`)
  const { confirm } = useKit()
  const proof = Object.entries(r.evidence ?? {}).filter(([k]) => k !== 'preview')
  const link = async (c: { id: string; name: string }) => {
    const ok = await confirm({ title: `Link to ${c.name}`, confirm: 'Link', body: <>{r.title} goes to {c.name}. Its spend counts for them from here on.</> })
    if (ok.ok) decide.mutate({ id: r.id!, body: { outcome: 'link', company_id: c.id }, done: `Linked to ${c.name}` })
  }
  const reject = async () => {
    const ok = await confirm({ title: "Don't link", confirm: "Don't link", danger: true, body: <>{r.title} stays unlinked.</> })
    if (ok.ok) decide.mutate({ id: r.id!, body: { outcome: 'reject' }, done: 'Left unlinked' })
  }
  return (
    <>
      <p className="st-td-sub st-td-facts">{REASON[r.reason ?? ''] ?? 'Needs a look'}{r.account_domain ? `, domain ${r.account_domain}` : ''}</p>
      {proof.length ? (
        <dl className="st-facts st-td-proof" data-testid="staff-case-evidence">
          {proof.map(([k, v]) => <div key={k} className="st-td-proof-row"><dt>{EVIDENCE[k] ?? k.replace(/_/g, ' ')}</dt><dd>{evidenceValue(v)}</dd></div>)}
        </dl>
      ) : <p className="st-td-sub st-td-facts" data-testid="staff-case-evidence">No evidence stored</p>}
      <div className="st-td-actions">
        {r.companies?.map((c) => <Button key={c.id} disabled={decide.isPending} onClick={() => link(c)} data-testid="staff-case-link">Link to {c.name} ({c.note})</Button>)}
        <Button tone="quiet" disabled={decide.isPending} onClick={reject} data-testid="staff-case-reject">Don't link</Button>
      </div>
    </>
  )
}

function JobActions({ r }: { r: RowT }) {
  const retry = useRowAction<undefined>((id) => `/api/staff/jobs/${id}/retry`)
  return <div className="st-td-actions"><span className="st-td-sub">{num(r.attempts)} {r.attempts === 1 ? 'try' : 'tries'}</span><Button disabled={retry.isPending} onClick={() => retry.mutate({ id: r.id!, done: 'Queued again' })} data-testid="staff-job-retry">Retry</Button></div>
}

function TaskActions({ r }: { r: RowT }) {
  const done = useRowAction<{ done: boolean }>((id) => `/api/staff/crm/tasks/${id}/done`)
  return <div className="st-td-actions"><Button tone="quiet" disabled={done.isPending} onClick={() => done.mutate({ id: r.id!, body: { done: true }, done: 'Task done' })} data-testid="today-task-done">Done</Button></div>
}

type InboxSetup = { client_id: string | null; client_secret_set: boolean; redirect_uri: string; scope: string }

/** What the Gmail connect sends Google. The redirect URI must be listed in the Google Cloud console. */
function InboxHealth() {
  const q = useQuery({ queryKey: ['staff', 'inbox-health'], queryFn: () => api<InboxSetup>('GET', '/api/inbox/health') })
  const g = q.data
  return (
    <details className="st-td-details" id="inbox-health" data-testid="staff-inbox-health">
      <summary>Inbox set-up</summary>
      {q.error ? <Err error={q.error} /> : g ? (
        <dl className="st-facts">
          <dt>Gmail client id</dt><dd>{g.client_id ?? 'Missing'}</dd>
          <dt>Gmail client secret</dt><dd>{g.client_secret_set ? 'Set' : 'Missing'}</dd>
          <dt>Redirect URI Google must list</dt><dd>{g.redirect_uri}</dd>
          <dt>Access asked for</dt><dd>{g.scope}</dd>
        </dl>
      ) : <Busy />}
    </details>
  )
}

const FEED_SHOWN = 6

function Feed() {
  const [kind, setKind] = useState('')
  const [pages, setPages] = useState<FeedRow[][]>([])
  const [next, setNext] = useState<string | null | undefined>(undefined)
  const [shown, setShown] = useState(FEED_SHOWN)
  const q = useQuery({
    queryKey: ['staff', 'today', 'feed', kind],
    queryFn: () => api<{ at: string; rows: FeedRow[]; next_before: string | null }>('GET', `/api/staff/today/feed?kind=${kind}`),
    // Once older pages are held, a refresh of the first page could open a gap between them, so it stops.
    refetchInterval: pages.length ? false : 15_000,
  })
  const rows = [...(q.data?.rows ?? []), ...pages.flat()]
  const before = next === undefined ? q.data?.next_before : next
  const older = async () => {
    if (rows.length <= shown && before) {
      const r = await api<{ rows: FeedRow[]; next_before: string | null }>('GET', `/api/staff/today/feed?kind=${kind}&before=${encodeURIComponent(before)}`)
      setPages((p) => [...p, r.rows])
      setNext(r.next_before)
    }
    setShown((n) => n + FEED_SHOWN)
  }
  return (
    <section className="st-td-group st-td-feed" data-testid="staff-feed">
      <div className="st-section-head">
        <h2 className="st-section-title">Done recently</h2>
        <span className="st-row">
          <select className="st-select" value={kind} aria-label="Show" data-testid="staff-feed-filter" onChange={(e) => { setKind(e.target.value); setPages([]); setNext(undefined); setShown(FEED_SHOWN) }}>
            {Object.entries(FEED_KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </span>
      </div>
      {q.isLoading ? <Busy /> : q.error ? <Err error={q.error} /> : rows.length === 0 ? <p className="st-note">Nothing yet</p> : (
        <ol className="st-td-feedlist">
          {rows.slice(0, shown).map((r, i) => (
            <li key={`${r.path}-${r.at}-${i}`} data-testid="staff-feed-item">
              <span className="st-tl-when" title={when(r.at)}>{ago(r.at)}</span>
              <span><Link className="st-link" to={r.path}>{r.title}</Link>{r.sub ? <span className="st-td-sub"> {r.sub.replace(/_/g, ' ')}</span> : null}</span>
            </li>
          ))}
        </ol>
      )}
      {rows.length > shown || before ? <div><Button tone="quiet" onClick={older} data-testid="staff-feed-older">Older</Button></div> : null}
    </section>
  )
}
